import { useId } from 'react'

import { ATTRIBUTION_GROUP_BY_LABELS, type AttributionGroupBy, type AttributionReport } from '@/api/attribution'
import { ATTRIBUTION_MODEL_LABELS, ATTRIBUTION_MODELS, type AttributionModel } from '@/api/types'
import { tmtAttributedLabel } from '@/components/TmtAttributedBadge'
import { Card, DataTable, LoadingBlock, type Column } from '@/design'

import { ShareBadge, TmtBadge } from './AttributionBadges'
import { AttributionError, NoSales } from './AttributionStates'
import type { ModelComparisonReads } from './attributionQuery'
import { NameCell, Sourced, type SourceRef } from './cells'
import {
  comparisonRows,
  isEmptyReport,
  rowKey,
  shareHeading,
  splitsNotTracked,
  totalsAgree,
  UNATTRIBUTED_LABELS,
  type ComparisonRow,
} from './definitions'
import { count, money, unattributedShareText } from './format'

type ModelReports = ReadonlyArray<{ model: AttributionModel; report: AttributionReport }>

/** Each model's source, described by its footer entry, and the spread's, described by the note. */
type Sources = Record<AttributionModel, SourceRef> & { spread: SourceRef }

function columnsFor(report: AttributionReport, sources: Sources): Column<ComparisonRow>[] {
  const { groupBy, currency } = report
  const perModel = ATTRIBUTION_MODELS.flatMap((model): Column<ComparisonRow>[] => [
    {
      key: `revenue:${model}`,
      header: `Revenue · ${ATTRIBUTION_MODEL_LABELS[model]}`,
      align: 'end',
      cell: (row) => <Sourced by={sources[model]}>{money(row.byModel[model].revenueMinor, currency)}</Sourced>,
    },
    {
      key: `purchases:${model}`,
      header: `Purchases · ${ATTRIBUTION_MODEL_LABELS[model]}`,
      align: 'end',
      cell: (row) => <Sourced by={sources[model]}>{count(row.byModel[model].purchases)}</Sourced>,
    },
  ])
  return [
    {
      key: 'name',
      header: ATTRIBUTION_GROUP_BY_LABELS[groupBy],
      cell: (row) => <NameCell label={row.label} kind={row.kind} report={report} />,
    },
    ...perModel,
    {
      key: 'spread',
      header: 'Revenue spread',
      align: 'end',
      cell: (row) => <Sourced by={sources.spread}>{money(row.spreadMinor, currency)}</Sourced>,
    },
  ]
}

/**
 * Each model's totals and unattributed share, side by side — the comparison's footer,
 * and the legend every cell under that model points at.
 */
function ModelFooter({ reports, sources }: { reports: ModelReports; sources: Sources }) {
  return (
    <dl className="attr-model-footer" aria-label="Totals by model">
      {reports.map(({ model, report }) => {
        const shareText = unattributedShareText(report)
        const split = splitsNotTracked(report)
        return (
          <div key={model} id={sources[model].describedBy} data-model={model}>
            <dt>
              <TmtBadge model={model} lookbackDays={report.lookbackDays} />
            </dt>
            <dd>
              {money(report.totals.revenueMinor, report.currency)} · {count(report.totals.purchases)} purchases
            </dd>
            {shareText ? (
              <>
                <dd>
                  {shareHeading(split)} {shareText.revenue} <ShareBadge report={report} />
                </dd>
                <dd>{shareText.detail}</dd>
              </>
            ) : null}
          </div>
        )
      })}
    </dl>
  )
}

/**
 * The same range and grouping under all four models at once (AC1's side-by-side view):
 * the same sales, credited differently, so a reader sees how much of each row's revenue
 * depends on the model rather than on the marketing.
 */
export function ModelComparison({ comparison, groupBy }: { comparison: ModelComparisonReads; groupBy: AttributionGroupBy }) {
  const base = useId()
  const sources = {
    ...(Object.fromEntries(
      ATTRIBUTION_MODELS.map((model) => [model, { source: tmtAttributedLabel(model), describedBy: `${base}-${model}` }]),
    ) as Record<AttributionModel, SourceRef>),
    spread: { source: tmtAttributedLabel('all'), describedBy: `${base}-spread` },
  }
  if (comparison.isPending) return <LoadingBlock rows={6} label="Loading all four attribution models" />
  if (!comparison.reports) return <AttributionError error={comparison.error} title="The model comparison could not be loaded" />
  const reports = comparison.reports
  const first = reports[0]!.report
  if (reports.every(({ report }) => isEmptyReport(report))) return <NoSales from={first.start} to={first.end} />
  return (
    <Card
      title="Model comparison"
      subtitle={`${ATTRIBUTION_GROUP_BY_LABELS[groupBy]} · ${first.start} to ${first.end} · production, all websites`}
    >
      <p role="note" className="attr-note" id={sources.spread.describedBy}>
        {totalsAgree(reports)
          ? `The same sales, credited differently: every model's totals are the same ${money(first.totals.revenueMinor, first.currency)} across ${count(first.totals.purchases)} purchases. Only which row gets the credit changes.`
          : 'The four models do not total the same for this range — the nightly attribution rebuild may be part-way through. Each model’s own totals are below.'}{' '}
        {splitsNotTracked(first)
          ? 'The Not tracked row is the sales with no tracked visit to credit, kept apart from direct visits.'
          : `The unattributed share is the “${UNATTRIBUTED_LABELS[groupBy]}” row.`}{' '}
        Revenue spread is the most revenue any model credits a row, less the least: how much of it depends on the model.
      </p>
      <DataTable
        caption={`Revenue and purchases by ${ATTRIBUTION_GROUP_BY_LABELS[groupBy].toLowerCase()} under each model`}
        rows={comparisonRows(reports, groupBy)}
        rowKey={rowKey}
        columns={columnsFor(first, sources)}
      />
      <ModelFooter reports={reports} sources={sources} />
    </Card>
  )
}
